/**
 * Collapses one dealer's message, forwarded into many groups, into one card.
 *
 * A dealer posts the same stock into every group they are in, so a single
 * Sky-Dweller can arrive eight times — same sender, same words, eight group
 * names. The feed showed eight cards and buried everything else.
 *
 * WHAT COUNTS AS THE SAME MESSAGE
 *
 * Same sender AND exactly the same text. Both halves are deliberate:
 *
 *   - Keyed on the SENDER, because two different dealers posting identical
 *     wording are two real offers. Collapsing those would hide supply, which
 *     is the opposite of what this page is for.
 *   - EXACT text, no normalising. A dealer who edits a price between posts has
 *     said something different, and guessing at "close enough" is how a feed
 *     starts hiding messages nobody can find again.
 *
 * WHY THIS RUNS OVER EVERY LOADED ITEM, NOT A PAGE
 *
 * useTradeFeed accumulates pages into one array, so collapsing the whole array
 * also catches copies that straddle a page boundary. It cannot catch a copy
 * that has not been fetched yet — that is the known limit of doing this in the
 * client, and the reason the honest fix is eventually to mark duplicates at
 * ingest.
 *
 * ORDER IS PRESERVED. The feed is newest-first, so the FIRST copy encountered
 * is the most recent one; that is the copy kept, and it stays exactly where it
 * was. Keeping the oldest instead would make a card jump backwards in time the
 * moment a second copy loaded.
 */

/**
 * The join between sender and text.
 *
 * A character that cannot occur in either, so "44x" + "7y" can never collide
 * with "44" + "x7y". A plain concatenation would.
 */
const SEP = "\u0000";

/**
 * @param {Array<object>} items  cards, newest first
 * @returns {Array<object>} the same cards with duplicates folded in, each
 *   survivor carrying `forwarded_to` (every group it appeared in, in the order
 *   seen) and `forward_count`
 */
export default function collapseForwards(items) {
  if (!Array.isArray(items) || items.length === 0) return [];

  const byKey = new Map();
  const out = [];

  for (const item of items) {
    const text = typeof item?.message_text === "string" ? item.message_text : "";
    const sender = typeof item?.sender_phone === "string" ? item.sender_phone : "";

    /*
     | A message with no text is never a duplicate of another.
     |
     | Keying on sender + "" would fold every one of a dealer's image-only
     | messages into a single card — a dozen different watches hidden behind
     | one photo. The same goes for a message whose sender we do not know: with
     | no phone we cannot say two messages came from the same person.
     */
    if (text === "" || sender === "") {
      out.push(item);
      continue;
    }

    const key = sender + SEP + text;
    const seen = byKey.get(key);

    if (!seen) {
      // The survivor. A copy, so the caller's data is not mutated — these
      // objects come straight from the hook's state.
      const kept = { ...item, forwarded_to: item.group_name ? [item.group_name] : [], forward_count: 1 };
      byKey.set(key, kept);
      out.push(kept);
      continue;
    }

    seen.forward_count += 1;

    // De-duplicated: the same group can legitimately deliver twice, and
    // "+2 groups" naming one group would be wrong.
    if (item.group_name && !seen.forwarded_to.includes(item.group_name)) {
      seen.forwarded_to.push(item.group_name);
    }

    /*
     | Badges are OR-ed across the copies, not taken from the survivor.
     |
     | They record what happened to a message after it arrived — somebody
     | logged a trade price, somebody raised a lead — and that is recorded
     | against the one copy they happened to be looking at. Keeping only the
     | newest copy's badges would silently drop the fact that this offer
     | already produced a price, which is exactly the thing worth seeing.
     |
     | The merged card is one message as far as the reader is concerned, so it
     | should carry everything that message produced.
     */
    if (item.badges) {
      seen.badges = {
        ...seen.badges,
        trade_price_added: Boolean(seen.badges?.trade_price_added || item.badges.trade_price_added),
        lead_present: Boolean(seen.badges?.lead_present || item.badges.lead_present),
      };
    }

    // The PRICE goes with the badge, for the same reason. Only the badge was
    // carried at first, so a merged card said "Trade price added" with no
    // price whenever the price was logged against an older copy (Alex,
    // 2026-10-20). An approved price on any copy beats an extracted one.
    if (!seen.prices?.length && item.prices?.length) {
      seen.prices = item.prices;
      seen.price_count = item.price_count;
      seen.approved_count = item.approved_count;
      seen.unapproved_count = item.unapproved_count;
    }
    if (!seen.trade_prices?.length && item.trade_prices?.length) {
      seen.trade_prices = item.trade_prices;
    }
    if (!seen.extracted_prices?.length && item.extracted_prices?.length) {
      seen.extracted_prices = item.extracted_prices;
      seen.extracted_count = item.extracted_count;
    }
  }

  return out;
}
